<template>
  <el-dialog
      :visible.sync="isVisible"
      width="680px"
      @closed="resetModal"
      custom-class="ia-modal"
  >
    <div slot="title" class="modal-header-container">
      <div class="custom-modal-title">
        <i class="el-icon-magic-stick"></i> {{ $t('socialPosts.create.modalIaPost.title') }}
      </div>
      <div class="subtitle-header">
        <span v-if="currentStep === 'CHOICE'">
          {{ $t('socialPosts.create.modalIaPost.subtitleChoice') }}
        </span>
        <span v-else-if="currentStep === 'SUGGESTIONS'">
          {{ $t('socialPosts.create.modalIaPost.subtitleSuggestions') }}
        </span>
        <span v-else-if="currentStep === 'IDEA'">
          {{ $t('socialPosts.create.modalIaPost.subtitleIdea') }}
        </span>
      </div>
    </div>

    <div v-if="currentStep !== 'CHOICE'" class="back-button-container">
      <el-button type="text" class="btn-back" @click="currentStep = 'CHOICE'">
        <i class="el-icon-back"></i> {{ $t('socialPosts.create.modalIaPost.back') }}
      </el-button>
    </div>

    <div v-if="currentStep === 'CHOICE'">
      <p class="step-instruction">{{ $t('socialPosts.create.modalIaPost.stepInstruction') }}</p>

      <div class="choice-card" @click="currentStep = 'IDEA'">
        <div class="choice-icon icon-idea"><i class="el-icon-s-opportunity"></i></div>
        <div class="choice-text">
          <h4>{{ $t('socialPosts.create.modalIaPost.cardIdeaTitle') }}</h4>
          <p>{{ $t('socialPosts.create.modalIaPost.cardIdeaDescription') }}</p>
        </div>
      </div>

      <div class="choice-card" @click="openSuggestionsStep">
        <div class="choice-icon icon-magic"><i class="el-icon-magic-stick"></i></div>
        <div class="choice-text">
          <h4>{{ $t('socialPosts.create.modalIaPost.cardSuggestionsTitle') }}</h4>
          <p>{{ $t('socialPosts.create.modalIaPost.cardSuggestionsDescription') }}</p>
        </div>
      </div>
    </div>

    <div v-if="currentStep === 'IDEA'">
      <h4 class="step-title">{{ $t('socialPosts.create.modalIaPost.ideaTitle') }}</h4>
      <el-input
          type="textarea"
          :rows="5"
          class="custom-textarea"
          :placeholder="ideaPlaceholder"
          v-model="userPrompt">
      </el-input>
      <p class="help-text-bottom">{{ $t('socialPosts.create.modalIaPost.ideaHelp') }}</p>
    </div>

    <div v-if="currentStep === 'SUGGESTIONS'">
      <h4 class="step-title">{{ $t('socialPosts.create.modalIaPost.themeTitle') }}</h4>
      <div class="themes-grid-wrapper" v-loading="themesLoading">
        <div class="themes-grid">
          <span
              v-for="theme in themesList"
              :key="theme.id"
              class="theme-button"
              :class="{ 'is-selected': selectedTheme === sujetId(theme) }"
              @click="selectTheme(theme)">
            <span class="theme-button__label">{{ theme.title }}</span>
            <i
                v-if="selectedTheme === sujetId(theme)"
                class="el-icon-close theme-button__close"
                @click.stop="clearSelectedTheme()">
            </i>
          </span>
        </div>
      </div>

      <h4 class="step-title-secondary">{{ $t('socialPosts.create.modalIaPost.optionalTitle') }}</h4>
      <el-input
          type="textarea"
          :rows="3"
          class="custom-textarea"
          :placeholder="$t('socialPosts.create.modalIaPost.optionalPlaceholder')"
          v-model="optionalPrecisions">
      </el-input>
      <p class="help-text-bottom">{{ $t('socialPosts.create.modalIaPost.optionalHelp') }}</p>
    </div>

    <span slot="footer" class="dialog-footer" v-if="currentStep !== 'CHOICE'">
      <el-button @click="isVisible = false" class="btn-cancel">{{ $t('socialPosts.create.modalIaPost.cancel') }}</el-button>
      <el-button
          type="primary"
          class="btn-submit-ia"
          :disabled="!isReadyToGenerate"
          @click="generateAndInsert">
        <i class="el-icon-magic-stick"></i>
        <span v-if="currentStep === 'IDEA'">{{ $t('socialPosts.create.modalIaPost.generateContent') }}</span>
        <span v-else>{{ $t('socialPosts.create.modalIaPost.viewProposal') }}</span>
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import { fetchContentLibraryItems } from '@/services/contentLibrary'

export default {
  name: 'ModalIAPost',
  data () {
    return {
      isVisible: false,
      currentStep: 'CHOICE',
      selectedTheme: null,
      optionalPrecisions: '',
      userPrompt: '',
      themesList: [],
      themesLoading: false
    }
  },
  computed: {
    ...mapGetters(['isVo', 'isCmi', 'isPromoteur']),
    ideaPlaceholder () {
      const base = 'socialPosts.create.modalIaPost.ideaPlaceholder'
      if (this.isVo) return this.$t(`${base}.vo`)
      if (this.isCmi) return this.$t(`${base}.cmi`)
      if (this.isPromoteur) return this.$t(`${base}.promoteur`)
      return this.$t(`${base}.immoAncien`)
    },
    isReadyToGenerate () {
      if (this.currentStep === 'IDEA') return this.userPrompt.trim().length > 0
      if (this.currentStep === 'SUGGESTIONS') return this.selectedTheme !== null
      return false
    }
  },
  methods: {
    sujetId (sujet) {
      return sujet != null && sujet.id != null ? String(sujet.id) : ''
    },
    sujetInsertText (sujet) {
      if (!sujet) return ''
      const desc = sujet.description
      if (desc != null && String(desc).trim() !== '') return desc
      return sujet.instruction || ''
    },
    async loadThemesFromApi () {
      this.themesLoading = true
      try {
        const items = await fetchContentLibraryItems()
        this.themesList = items.filter(item => !item.state || item.state === 'ACTIVE')
      } catch (e) {
        this.themesList = []
        this.$message({
          message: this.$t('socialPosts.create.modalIaPost.themesFetchFailed'),
          type: 'info',
          duration: 8000
        })
      } finally {
        this.themesLoading = false
      }
    },
    restoreSujetFromStore () {
      const fromStore = this.$store.state.socialPostsCreationPostEditorial.post.initial.studioSujetId
      this.selectedTheme = fromStore != null && fromStore !== '' ? String(fromStore) : null
      const promptFromStore = this.$store.state.socialPostsCreationPostEditorial.post.initial.promptUtilisateur
      this.optionalPrecisions = promptFromStore != null ? String(promptFromStore) : ''
    },
    selectTheme (theme) {
      this.selectedTheme = this.sujetId(theme)
    },
    clearSelectedTheme () {
      this.selectedTheme = null
      this.$store.commit('socialPostsCreationPostEditorial/clearStudioSujetContext')
      this.$store.commit('socialPostsCreationPostEditorial/updateInputTextInitial', '')
      this.$store.commit('socialPostsCreationPostEditorial/checkDraftButtonState', '')
      this.$store.commit('socialPostsCreationPostEditorial/checkPublishButtonState', '')
    },
    openSuggestionsStep () {
      this.currentStep = 'SUGGESTIONS'
      this.restoreSujetFromStore()
      this.loadThemesFromApi()
    },
    init (step, themeId = null) {
      this.currentStep = step
      if (themeId != null && themeId !== '') {
        this.selectedTheme = String(themeId)
      } else {
        this.restoreSujetFromStore()
      }
      this.isVisible = true
      if (step === 'SUGGESTIONS') {
        this.loadThemesFromApi()
      }
    },
    buildGenerationSnapshot () {
      if (this.currentStep === 'IDEA') {
        return { step: 'IDEA', userPrompt: this.userPrompt.trim() }
      }
      return {
        step: 'SUGGESTIONS',
        selectedTheme: this.selectedTheme,
        optionalPrecisions: this.optionalPrecisions,
        themesList: this.themesList.slice()
      }
    },
    runIdeaFromUserPrompt (snap) {
      this.isVisible = false
      this.$store.dispatch('socialPostsCreationPostEditorial/dispatchEditorialIaGeneration', {
        network: 'initial',
        initialIaPrompt: snap.userPrompt
      })
    },
    runSujetFromBibliotheque (snap) {
      this.isVisible = false
      this.$store.dispatch('socialPostsCreationPostEditorial/dispatchEditorialIaGeneration', {
        network: 'initial',
        studioSujetId: snap.selectedTheme,
        promptUtilisateur: (snap.optionalPrecisions || '').trim()
      })
    },
    generateAndInsert () {
      const snap = this.buildGenerationSnapshot()
      if (snap.step === 'IDEA') {
        this.runIdeaFromUserPrompt(snap)
        return
      }
      this.runSujetFromBibliotheque(snap)
    },
    resetModal () {
      this.currentStep = 'CHOICE'
      this.selectedTheme = null
      this.optionalPrecisions = ''
      this.userPrompt = ''
    }
  }
}
</script>

<style scoped lang="scss">
/* --- Variables & Reset --- */
$color-purple: #a855f7;

::v-deep .ia-modal {
  border-radius: 4px !important;
  overflow: hidden;
}

::v-deep .el-dialog__header {
  padding: 20px 25px 5px 25px;
  text-align: left !important;
}

::v-deep .el-dialog__body {
  padding: 15px 25px 25px 25px;
}

/* --- Header & Titres --- */
.custom-modal-title {
  display: flex;
  align-items: center;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-black);
  margin-bottom: 4px;
  text-transform: none !important;

  i {
    color: var(--color-blue);
    font-size: 1.5rem;
    margin-right: 10px;
  }
}

.subtitle-header {
  font-size: 14px;
  color: var(--color-text-muted);
  text-transform: none !important;
  line-height: 1.2;
}

.step-title, .step-title-secondary, .choice-text h4 {
  text-transform: none !important;
  text-align: left;
  font-weight: 600;
  color: var(--color-black);
}

.step-title-secondary {
  margin-top: 20px;
}

.step-instruction, .help-text-bottom {
  text-align: left;
  font-size: 13px;
  font-weight: 400;
  color: var(--color-black);
  margin-bottom: 20px;
}

/* --- Cartes --- */
.choice-card {
  display: flex;
  align-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 20px;
  margin-bottom: 15px;
  cursor: pointer;
  background: #fff;
  transition: all 0.2s;

  &:hover { border-color: var(--color-blue); box-shadow: 0 4px 12px rgba(0,0,0,0.05); }

  .choice-icon {
    width: 60px; height: 60px; min-width: 60px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 50%; margin-right: 20px;
    i { font-size: 28px; }
  }

  .icon-idea { background: #e6fffa; i { color: var(--color-blue) !important; } }
  .icon-magic { background: #f3e8ff; i { color: $color-purple !important; } }

  .choice-text {
    text-align: left;
    h4 { margin: 0 0 5px 0; font-size: 16px; font-weight: 600; }
    p { margin: 0; font-size: 13px; font-weight: 400; color: var(--color-black); }
  }
}

/* --- Thèmes (Tags) --- */
.themes-grid-wrapper {
  max-height: 180px;
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: 5px;
  padding: 12px;
  background-color: #f3f4f6;
  border-radius: 6px;
  display: block;

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
  }
}

.themes-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
  .theme-button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    padding: 10px 15px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background-color: #fff;
    color: var(--color-black);
    font-size: 14px;
    cursor: pointer;
    user-select: none;
    transition: border-color 0.2s, color 0.2s, background-color 0.2s;

    &__label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__close {
      flex-shrink: 0;
      font-size: 11px;
      color: inherit;
      opacity: 0.75;
      &:hover {
        opacity: 1;
      }
    }

    &:hover:not(.is-selected) {
      border-color: var(--color-blue);
      color: var(--color-blue);
    }

    &.is-selected {
      background-color: var(--color-blue);
      border-color: var(--color-blue);
      color: #fff;

      .theme-button__label,
      .theme-button__close {
        color: #fff;
      }
    }
  }
}
.back-button-container {
  text-align: left;
  margin-bottom: 15px;
}

.btn-back {
  color: var(--color-black);
  font-weight: 600;
  padding-left: 0;
  i { margin-right: 5px; }
}

/* --- Textarea & Buttons --- */
::v-deep .custom-textarea .el-textarea__inner {
  background: #f3f4f6;
  border: 1px solid transparent;
  border-radius: 4px;
  &:focus {
    background: #fff;
    border-color: var(--color-blue);
  }
}

.btn-back {
  color: var(--color-black);
  font-weight: 600;
  i {
    margin-right: 5px;
  }
}

.btn-submit-ia {
  background: var(--color-blue) !important;
  border-color: var(--color-blue) !important;
  font-weight: 600;
  border-radius: 4px;
  i {
    margin-right: 8px;
  }
}
</style>
